import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Tag,
  Space,
  Typography,
  Timeline,
  Row,
  Col,
  Descriptions,
  Progress,
  message,
  Spin,
  Divider,
  Input,
  Tooltip,
  Badge,
} from 'antd';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  RobotOutlined,
  SendOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
  CloseCircleOutlined,
  MailOutlined,
  EditOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import type { Reply, SendTask, OutreachMessage } from '../../types/outreach';
import { outreachApi } from '../../services/outreach';
import { ROUTES } from '../../config/routes';
import { COLORS } from '../../constants/colors';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// 意向标签配置（4种新枚举: positive / neutral / negative / follow_up）
const sentimentConfig: Record<string, { color: string; text: string; icon: React.ReactNode }> = {
  positive: { color: 'success', text: '积极', icon: <CheckCircleOutlined /> },
  neutral: { color: 'default', text: '中性', icon: <RobotOutlined /> },
  negative: { color: 'error', text: '消极', icon: <CloseCircleOutlined /> },
  follow_up: { color: 'warning', text: '待跟进', icon: <QuestionCircleOutlined /> },
};

export const ReplyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState<Reply | null>(null);
  const [sendTask, setSendTask] = useState<SendTask | null>(null);
  const [originalMessage, setOriginalMessage] = useState<OutreachMessage | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [generatingFollowUp, setGeneratingFollowUp] = useState(false);
  const [followUpContent, setFollowUpContent] = useState('');
  const [editingFollowUp, setEditingFollowUp] = useState(false);

  useEffect(() => {
    if (id) {
      fetchReplyDetail();
    }
  }, [id]);

  const fetchReplyDetail = async () => {
    setLoading(true);
    try {
      // 获取回复详情
      const replyResult = await outreachApi.getReply(id!);
      if (replyResult.data) {
        setReply(replyResult.data);
        
        // 获取发送任务详情
        try {
          const tasksResult = await outreachApi.listSendTasks(replyResult.data.send_task_id, { page: 1, page_size: 1 });
          if (tasksResult.data?.list?.length > 0) {
            setSendTask(tasksResult.data.list[0]);
          }
        } catch (error) {
          console.error('获取发送任务失败:', error);
        }
      }
    } catch (error) {
      console.error('获取回复详情失败:', error);
      message.error('获取回复详情失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!id) return;
    setAnalyzing(true);
    try {
      await outreachApi.analyzeReply(id);
      message.success('分析已启动，请稍后刷新查看结果');
      // 3秒后刷新
      setTimeout(fetchReplyDetail, 3000);
    } catch (error) {
      console.error('分析失败:', error);
      message.error('分析失败');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGenerateFollowUp = async () => {
    if (!id) return;
    setGeneratingFollowUp(true);
    try {
      await outreachApi.generateFollowUp(id);
      message.success('跟进内容生成中，请稍后刷新查看');
      // 3秒后刷新
      setTimeout(fetchReplyDetail, 3000);
    } catch (error) {
      console.error('生成跟进内容失败:', error);
      message.error('生成跟进内容失败');
    } finally {
      setGeneratingFollowUp(false);
    }
  };

  const handleSaveFollowUp = async () => {
    if (!id || !followUpContent) return;
    try {
      await outreachApi.updateFollowUp(id, { content: followUpContent });
      message.success('跟进内容已保存');
      setEditingFollowUp(false);
      fetchReplyDetail();
    } catch (error) {
      console.error('保存跟进内容失败:', error);
      message.error('保存失败');
    }
  };

  const handleMarkAsRead = async () => {
    if (!id) return;
    try {
      await outreachApi.markReplyRead(id);
      message.success('已标记为已读');
      fetchReplyDetail();
    } catch (error) {
      console.error('标记已读失败:', error);
      message.error('标记已读失败');
    }
  };

  const getSentimentTag = (reply: Reply) => {
    const sentiment = reply.ai_analysis?.sentiment || reply.intent_classification || 'unknown';
    const config = sentimentConfig[sentiment] || { color: 'default', text: '未分析', icon: null };
    
    return (
      <Tag color={config.color} icon={config.icon} style={{ fontSize: 14, padding: '4px 12px' }}>
        {config.text}
      </Tag>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!reply) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Text type="secondary">回复不存在或已被删除</Text>
      </div>
    );
  }

  const aiAnalysis = reply.ai_analysis;
  const sentiment = aiAnalysis?.sentiment || reply.intent_classification;

  return (
    <div>
      {/* 顶部导航 */}
      <div style={{ marginBottom: 24 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate(ROUTES.OUTREACH_REPLIES)}
        >
          返回列表
        </Button>
      </div>

      {/* 标题栏 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          <MailOutlined style={{ marginRight: 8 }} />
          回复详情
          {!reply.is_read && <Badge status="processing" style={{ marginLeft: 8 }} />}
        </Title>
        <Space>
          {!reply.is_read && (
            <Button onClick={handleMarkAsRead}>
              标记已读
            </Button>
          )}
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchReplyDetail}
          >
            刷新
          </Button>
        </Space>
      </div>

      <Row gutter={24}>
        {/* 左侧：沟通时间线 */}
        <Col span={14}>
          <Card title="沟通时间线" style={{ marginBottom: 24 }}>
            <Timeline mode="left">
              {/* 原始发送 */}
              <Timeline.Item
                dot={<MailOutlined style={{ fontSize: 16 }} />}
                label={sendTask?.sent_at ? new Date(sendTask.sent_at).toLocaleString('zh-CN') : '-'}
              >
                <Card size="small" style={{ background: COLORS.bgSuccessLight }}>
                  <Text strong>发送邀约</Text>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">收件人: {reply.from_email}</Text>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">主题: {reply.subject || '无主题'}</Text>
                  </div>
                </Card>
              </Timeline.Item>

              {/* 回复 */}
              <Timeline.Item
                dot={<MailOutlined style={{ fontSize: 16, color: COLORS.primary }} />}
                label={reply.received_at ? new Date(reply.received_at).toLocaleString('zh-CN') : '-'}
                color="blue"
              >
                <Card size="small" style={{ background: COLORS.bgPrimaryLight }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text strong>收到回复</Text>
                    {getSentimentTag(reply)}
                  </div>
                  <Divider style={{ margin: '12px 0' }} />
                  <div style={{ background: COLORS.white, padding: 12, borderRadius: 4 }}>
                    <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {reply.reply_content}
                    </Paragraph>
                  </div>
                </Card>
              </Timeline.Item>

              {/* AI 分析 */}
              {aiAnalysis && (
                <Timeline.Item
                  dot={<RobotOutlined style={{ fontSize: 16, color: COLORS.purple }} />}
                  label="AI 分析"
                  color="purple"
                >
                  <Card size="small" style={{ background: COLORS.bgPurpleLight }}>
                    <Text strong>AI 意向分析</Text>
                    <div style={{ marginTop: 8 }}>
                      <Text>{aiAnalysis.summary}</Text>
                    </div>
                  </Card>
                </Timeline.Item>
              )}

              {/* 跟进记录 */}
              {reply.follow_up_content && (
                <Timeline.Item
                  dot={<SendOutlined style={{ fontSize: 16, color: COLORS.success }} />}
                  label="跟进"
                  color="green"
                >
                  <Card size="small" style={{ background: COLORS.bgSuccessLight }}>
                    <Text strong>跟进邮件</Text>
                    <div style={{ marginTop: 8, background: COLORS.white, padding: 12, borderRadius: 4 }}>
                      <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {reply.follow_up_content}
                      </Paragraph>
                    </div>
                  </Card>
                </Timeline.Item>
              )}
            </Timeline>
          </Card>
        </Col>

        {/* 右侧：AI 分析和操作 */}
        <Col span={10}>
          {/* AI 分析结果 */}
          <Card 
            title={
              <Space>
                <RobotOutlined />
                AI 意向分析
              </Space>
            }
            extra={
              !aiAnalysis?.sentiment && (
                <Button
                  type="primary"
                  size="small"
                  icon={<ReloadOutlined />}
                  loading={analyzing}
                  onClick={handleAnalyze}
                >
                  分析意向
                </Button>
              )
            }
            style={{ marginBottom: 24 }}
          >
            {aiAnalysis ? (
              <div>
                {/* 意向标签和置信度 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  {getSentimentTag(reply)}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Text type="secondary">置信度:</Text>
                    <Progress 
                      percent={Math.round((aiAnalysis.confidence || 0) * 100)} 
                      size="small" 
                      style={{ width: 100 }}
                      status={aiAnalysis.confidence && aiAnalysis.confidence >= 0.8 ? 'success' : 'normal'}
                    />
                  </div>
                </div>

                <Divider style={{ margin: '12px 0' }} />

                {/* 关键信息 */}
                <Descriptions column={1} size="small" labelStyle={{ fontWeight: 500 }}>
                  {aiAnalysis.key_info?.budget_intent && (
                    <Descriptions.Item label="预算意向">
                      {aiAnalysis.key_info.budget_intent}
                    </Descriptions.Item>
                  )}
                  {aiAnalysis.key_info?.timeline && (
                    <Descriptions.Item label="时间安排">
                      {aiAnalysis.key_info.timeline}
                    </Descriptions.Item>
                  )}
                  {aiAnalysis.key_info?.conditions && (
                    <Descriptions.Item label="合作条件">
                      {aiAnalysis.key_info.conditions}
                    </Descriptions.Item>
                  )}
                  {aiAnalysis.key_info?.questions && aiAnalysis.key_info.questions.length > 0 && (
                    <Descriptions.Item label="提出的问题">
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        {aiAnalysis.key_info.questions.map((q, i) => (
                          <li key={i}>{q}</li>
                        ))}
                      </ul>
                    </Descriptions.Item>
                  )}
                </Descriptions>

                <Divider style={{ margin: '12px 0' }} />

                {/* 建议行动 */}
                <div>
                  <Text strong>建议行动:</Text>
                  <div style={{ marginTop: 8, padding: 12, background: COLORS.gray100, borderRadius: 4 }}>
                    <Text>{aiAnalysis.suggested_action || reply.suggested_action || '暂无建议'}</Text>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <RobotOutlined style={{ fontSize: 48, color: COLORS.borderLight }} />
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary">尚未进行 AI 分析</Text>
                </div>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  loading={analyzing}
                  onClick={handleAnalyze}
                  style={{ marginTop: 16 }}
                >
                  开始分析
                </Button>
              </div>
            )}
          </Card>

          {/* 跟进操作 */}
          <Card 
            title={
              <Space>
                <SendOutlined />
                跟进操作
              </Space>
            }
            extra={
              reply.follow_up_content && !editingFollowUp && (
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setFollowUpContent(reply.follow_up_content || '');
                    setEditingFollowUp(true);
                  }}
                >
                  编辑
                </Button>
              )
            }
          >
            {editingFollowUp || !reply.follow_up_content ? (
              <div>
                <TextArea
                  rows={8}
                  placeholder="输入跟进邮件内容..."
                  value={followUpContent || reply.follow_up_content || ''}
                  onChange={(e) => setFollowUpContent(e.target.value)}
                  style={{ marginBottom: 16 }}
                />
                <Space>
                  {!reply.follow_up_content && (
                    <Button
                      icon={<RobotOutlined />}
                      loading={generatingFollowUp}
                      onClick={handleGenerateFollowUp}
                    >
                      AI 生成跟进
                    </Button>
                  )}
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSaveFollowUp}
                    disabled={!followUpContent && !reply.follow_up_content}
                  >
                    保存跟进
                  </Button>
                  {editingFollowUp && (
                    <Button onClick={() => setEditingFollowUp(false)}>
                      取消
                    </Button>
                  )}
                </Space>
              </div>
            ) : (
              <div>
                <div style={{ background: COLORS.bgSuccessLight, padding: 16, borderRadius: 4, marginBottom: 16 }}>
                  <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {reply.follow_up_content}
                  </Paragraph>
                </div>
                <Space>
                  <Button type="primary" icon={<SendOutlined />}>
                    发送跟进
                  </Button>
                  <Button onClick={() => {
                    setFollowUpContent(reply.follow_up_content || '');
                    setEditingFollowUp(true);
                  }}>
                    重新编辑
                  </Button>
                </Space>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};
